import dayjs from "dayjs";

import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import {
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation,
  useParams,
} from "react-router-dom";
import illustration2 from "src/assets/img/illustrations/illustr4.png";
import Button from "src/components/UI/Button/Button";
import PaddedView from "src/components/UI/PaddedView/PaddedView";
import FlightDealsFilterPanel from "src/components/features/cheap-flights/FlightDealsFilterPanel";
import { BreadcrumbNavigation } from "src/components/shared/navigation/BreadcrumbNav/BreadcrumbNavigation";
import { useDeviceContext } from "src/context/device-context";
import { useLocaleContext } from "src/context/locale-context";
import { buildLinkFromFilter } from "src/utils/cheap-flights";
import { FlightDealsList } from "../components/features/cheap-flights/FlightDealsList";
import { FlightMonthsFilter } from "../components/features/cheap-flights/FlightMonthsFilter";
import "./styles/CheapFlights.css";

const STR_VARS = {
  locationIds: "##LOCATION_IDS##",
  minPrice: "##MIN_PRICE##",
};

const variants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2 } },
};

export const CLICK_ACTION = {
  START_SEARCH: "START_SEARCH",
  SHOW_MORE: "SHOW_MORE",
};

function EmptyDeals({ hasCustomFilters, onResetSearch, onResetFilters }) {
  const { getStringRes } = useLocaleContext();
  return (
    <motion.div
      variants={variants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      className="cheap-flights-empty results-grid-item">
      <div className="graphics">
        <img src={illustration2} alt="" />
      </div>
      <h2>{getStringRes("res.notfound.search.message.heading")}</h2>
      <p>{getStringRes("res.notfound.search.message.text")}</p>
      <div className="actions">
        {hasCustomFilters && (
          <Button
            name={
              <span style={{ display: "flex", alignItems: "center", columnGap: "0.25rem" }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 12 12"
                  fill="none">
                  <g clipPath="url(#clip0_90_2229)">
                    <path
                      d="M6.00488 0C2.69117 0 0.00488281 2.68629 0.00488281 6C0.00488281 9.31371 2.69117 12 6.00488 12C9.31859 12 12.0049 9.31371 12.0049 6C12.0013 2.68777 9.31712 0.00358594 6.00488 0ZM8.00488 7.29349C8.20805 7.4805 8.22118 7.79681 8.03417 7.99999C7.84716 8.20317 7.53085 8.2163 7.32767 8.02929C7.3175 8.01994 7.30775 8.01016 7.29838 7.99999L6.00488 6.70699L4.71188 7.99999C4.51325 8.19183 4.19673 8.18632 4.00489 7.98769C3.81777 7.79393 3.81777 7.48676 4.00489 7.293L5.29789 6L4.00489 4.707C3.81305 4.50837 3.81856 4.19184 4.0172 4.00001C4.21095 3.81288 4.51813 3.81288 4.71188 4.00001L6.00488 5.29301L7.29838 4.00001C7.48538 3.79683 7.8017 3.7837 8.00488 3.97071C8.20805 4.15772 8.22118 4.47403 8.03417 4.67721C8.02482 4.68738 8.01505 4.69713 8.00488 4.70651L6.71187 6L8.00488 7.29349Z"
                      fill="white"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_90_2229">
                      <rect
                        width="12"
                        height="12"
                        fill="white"
                        transform="translate(0.00488281)"
                      />
                    </clipPath>
                  </defs>
                </svg>
                {getStringRes("res.filter.active.clear")}
              </span>
            }
            onClick={onResetFilters}
          />
        )}
        <Button
          name={getStringRes("cheap.flights.reset.default.search")}
          onClick={onResetSearch}
        />
      </div>
    </motion.div>
  );
}

function CheapFlights() {
  const { type, origin } = useParams();
  const {
    data,
    srcCityLocalised,
    destCityLocalised,
    destination,
    start,
    end,
    min,
    max,
    bestPrice,
  } = useLoaderData();
  const { getStringRes, currencySymbol, currentLocale } = useLocaleContext();
  const { isMobile } = useDeviceContext();
  const pageNavState = useLocation().state;

  const [expanded, setExpanded] = useState(pageNavState?.preExpanded);
  const [tripType, setTripType] = useState(type);
  const [priceEndRange] = useState([10, 1000]);
  const [selectedPriceRange, setPriceRange] = useState(() => {
    if ((min && max) || min === max) return [min, max];
    else if (min) return [min, priceEndRange[1]];
    else return [priceEndRange[0], max];
  });
  const [dateEndRange] = useState([
    dayjs().startOf("day").unix(),
    dayjs().add(11, "M").endOf("month").startOf("day").unix(),
  ]);
  const [selectedDateRange, setDepartureDateRange] = useState(() => {
    if ((start && end) || start === end) return [start, end];
    else if (start) return [start, dateEndRange[1]];
    else return [dateEndRange[0], end];
  });

  const [commitState, setCommitState] = useState({ price: false, date: false });
  const [location, setLocation] = useState({ origin, destination: destination });
  const navigate = useNavigate();
  const navigationState = useNavigation().state;

  let headerTitle = getStringRes("cheap.flights.subtitle").replace(
    STR_VARS.locationIds,
    destCityLocalised ? `${srcCityLocalised} - ${destCityLocalised}` : ` - ${srcCityLocalised}`
  );

  if (bestPrice) {
    headerTitle = headerTitle.replace(STR_VARS.minPrice, `${bestPrice}${currencySymbol}`);
  } else {
    headerTitle = null;
  }

  useEffect(() => {
    const header = document.querySelector(".cheap-flights-header");
    header.style.backgroundImage =
      `url("https://${window.SERVER_DATA.REACT_APP_BUCKET_NAME}.` +
      `${window.SERVER_DATA.REACT_APP_BUCKET_PATH}/static/` +
      `${window.SERVER_DATA.REACT_APP_TENANT_NAME}/en/assets/images/header_bg.svg")`;

    const topNavBar = document.getElementById("main-app-top-nav");
    topNavBar.classList.add("faq");
    return () => topNavBar.classList.remove("faq");
  }, []);

  useEffect(() => {
    const hasFilterChanged = (filters) => {
      return Object.values(filters).some((value) => value);
    };

    const getFiltersChanged = () => {
      return {
        price:
          (selectedPriceRange[0] !== min && commitState.price) ||
          (selectedPriceRange[1] !== max && commitState.price),
        date:
          (selectedDateRange[0] !== start && commitState.date) ||
          (selectedDateRange[1] !== end && commitState.date),
        tripType: tripType !== type,
        origin: origin !== location.origin,
        destination: destination !== location.destination,
      };
    };

    const changedFilters = getFiltersChanged();
    if (hasFilterChanged(changedFilters) && location.origin) {
      console.log("discovery changed filters");
      navigate(
        buildLinkFromFilter(
          tripType,
          location.origin,
          location.destination,
          selectedDateRange[0] === dateEndRange[0] && selectedDateRange[1] === dateEndRange[1]
            ? undefined
            : selectedDateRange[0],
          selectedDateRange[1] === dateEndRange[1] ? undefined : selectedDateRange[1],
          selectedPriceRange[0] === priceEndRange[0] ? undefined : selectedPriceRange[0],
          selectedPriceRange[1] === priceEndRange[1] ? undefined : selectedPriceRange[1],
          currentLocale.split("_")[0]
        ),
        { preventScrollReset: isMobile, state: { preExpanded: expanded } }
      );
    }
  }, [
    dateEndRange,
    destination,
    end,
    location.destination,
    location.origin,
    max,
    min,
    navigate,
    origin,
    priceEndRange,
    selectedDateRange,
    selectedPriceRange,
    start,
    tripType,
    type,
    commitState,
    isMobile,
    expanded,
    currentLocale,
  ]);

  const hasCustomFilters = () => {
    return (
      (selectedPriceRange[0] && selectedPriceRange[0] !== priceEndRange[0]) ||
      (selectedPriceRange[1] && selectedPriceRange[1] !== priceEndRange[1]) ||
      (selectedDateRange[0] && selectedDateRange[0] !== dateEndRange[0]) ||
      (selectedDateRange[1] && selectedDateRange[1] !== dateEndRange[1])
    );
  };

  const resetFilters = () => {
    setPriceRange([priceEndRange[0], priceEndRange[1]]);
    setDepartureDateRange([dateEndRange[0], dateEndRange[1]]);
    setCommitState({ price: true, date: true });
  };

  const resetSearch = () => navigate("/cheap-flights");

  return (
    <>
      <header className="cheap-flights-header">
        <PaddedView>
          <div className="header-content">
            <h1 className="header-title">{getStringRes("cheap.flights.title")}</h1>
            <h2 className="header-subtitle">{headerTitle}</h2>
          </div>
        </PaddedView>
      </header>
      <PaddedView>
        <div className="page-container">
          <BreadcrumbNavigation
            id="breadcrumb-nav-cheap-flights"
            steps={[
              { name: getStringRes("cheap.flights.breadcrumb.home"), link: "/" },
              {
                name: getStringRes("cheap.flights.breadcrumb.cheap.flights"),
                link: "/cheap-flights",
              },
              {
                name: destCityLocalised
                  ? `${srcCityLocalised} - ${destCityLocalised}`
                  : srcCityLocalised,
                link:
                  `/cheap-flights/${tripType}/${origin}` +
                  (destination ? `/${destination}` : ""),
              },
            ]}
            currentStep={2}
            unlockAllLinks
          />

          <div className="content-layout">
            <div
              className={`filters-container ${
                navigationState === "loading" ? "loading" : ""
              }`}>
              <FlightDealsFilterPanel
                srcCityLocalised={srcCityLocalised}
                destCityLocalised={destCityLocalised}
                tripType={tripType}
                setTripType={setTripType}
                setLocation={setLocation}
                selectedPriceRange={selectedPriceRange}
                setPriceRange={setPriceRange}
                selectedDateRange={selectedDateRange}
                setDepartureDateRange={setDepartureDateRange}
                commitState={commitState}
                setCommitState={setCommitState}
                priceEndRange={priceEndRange}
                dateEndRange={dateEndRange}
                expanded={expanded}
                setExpanded={setExpanded}
              />
            </div>

            <div className={`results-grid ${navigationState === "loading" ? "loading" : ""}`}>
              <FlightMonthsFilter
                tripType={tripType}
                location={location}
                priceEndRange={priceEndRange}
                selectedPriceRange={selectedPriceRange}
                buildLinkFromFilter={buildLinkFromFilter}
                expanded={expanded}
              />
              {data && data.length > 0 ? (
                <FlightDealsList
                  itemAction={
                    Boolean(destination) ? CLICK_ACTION.START_SEARCH : CLICK_ACTION.SHOW_MORE
                  }
                  deals={data}
                  type={type}
                />
              ) : (
                <EmptyDeals
                  onResetSearch={resetSearch}
                  onResetFilters={resetFilters}
                  hasCustomFilters={hasCustomFilters()}
                />
              )}
            </div>
          </div>
        </div>
      </PaddedView>
    </>
  );
}

export default CheapFlights;
